import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';
import { prop } from 'ramda';

import { JACKPOTS_REDUCER } from '@core/js/store/constants';
import { useCustomSelector, useUserSelector } from '@core/js/store/hooks';

import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { getGamesGlobalJackpots } from '@interlayer/js/components/common/Jackpot/buzz/server';
import * as actions from '@interlayer/js/components/common/Jackpot/store/actions.buzz';

import { GLOBAL_GAMES_JACKPOT_DEFAULT_PREFIX } from '../constants';
import { TJackpot } from '../types';

const updateTimeouts: Map<number, ReturnType<typeof setTimeout> | undefined> = new Map();

export const useUpdateJackpotData = (jackpot: TJackpot = {}, gamePayId?: number) => {
    const { moduleId, updateTimer } = jackpot;

    const dispatch = useDispatch();
    const currencyCode = useUserSelector(prop('currencyCode'));
    const { jackpotStartTime } = useCustomSelector(JACKPOTS_REDUCER);
    const [
        dTagGamesGlobalJackpotUrl,
        dTagGamesGlobalJackpotPrefix = GLOBAL_GAMES_JACKPOT_DEFAULT_PREFIX
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.gamesGlobalJackpotUrl,
        DYNAMIC_TAG_NAMES.gamesGlobalJackpotPrefix
    ]);

    useEffect(() => {
        if (!dTagGamesGlobalJackpotUrl || !currencyCode || !updateTimer || !moduleId || updateTimeouts.has(moduleId)) {
            return;
        }

        const timeout = updateTimer * 1000 - (Date.now() - jackpotStartTime);

        clearTimeout(updateTimeouts.get(moduleId));

        updateTimeouts.set(
            moduleId,
            setTimeout(() => {
                getGamesGlobalJackpots({ dTagGamesGlobalJackpotUrl, currencyCode, moduleId })
                    .then((jackpots) => {
                        dispatch(actions.fetchGamesGlobalJackpot({ jackpots, prefix: dTagGamesGlobalJackpotPrefix, gamePayId }));

                        dispatcher.dispatch('jackpots:gamesGlobal', jackpots);
                    });
            }, timeout)
        );

        return () => {
            if (updateTimeouts.has(moduleId)) {
                clearTimeout(updateTimeouts.get(moduleId));
                updateTimeouts.delete(moduleId);
            }
        };
    }, [
        dispatch,
        moduleId,
        gamePayId,
        updateTimer,
        currencyCode,
        jackpotStartTime,
        dTagGamesGlobalJackpotUrl,
        dTagGamesGlobalJackpotPrefix
    ]);
};
